import { formatNumberFundingRate, formatNumberUSD, formatPercentage } from "util/format"

import { PRICE_CHANGE_RATE_PRECISION } from "constant/number"
import { useAmmStatistics } from "container/service/StatisticsServiceContainer/useAmmStatistics"
import { useMemo } from "react"
import { useFundingUpdated } from "sdk-react/markets/useFundingUpdated"
import { createContainer } from "unstated-next"

const PLACEHOLDER = "-,---"

interface MarketInfoInitialState {
    tickerSymbol: string
}

export const MarketStatisticsContainer = createContainer(useMarketStatistics)

function useMarketStatistics({ tickerSymbol }: MarketInfoInitialState) {
    const { priceChangeRate24h, volume24h } = useAmmStatistics({ tickerSymbol })

    const volume24hStr = useMemo(() => {
        if (!volume24h) {
            return PLACEHOLDER
        }
        return formatNumberUSD({ num: volume24h })
    }, [volume24h])

    const priceChangeRate24hStr = useMemo(() => {
        if (!priceChangeRate24h) {
            return PLACEHOLDER
        }
        return `${formatPercentage(priceChangeRate24h, true, false, PRICE_CHANGE_RATE_PRECISION)}%`
    }, [priceChangeRate24h])

    const { isLoading, dailyFundingRateCapped } = useFundingUpdated({ tickerSymbol })

    const { fundingRate1hStr, fundingRate8hStr, fundingRate1dStr } = useMemo(() => {
        if (isLoading || !dailyFundingRateCapped) {
            return {
                fundingRate1hStr: PLACEHOLDER,
                fundingRate8hStr: PLACEHOLDER,
                fundingRate1dStr: PLACEHOLDER,
            }
        }

        const rate1h = dailyFundingRateCapped.div(24)
        const rate8h = dailyFundingRateCapped.div(3)
        const rate1d = dailyFundingRateCapped
        return {
            fundingRate1hStr: formatNumberFundingRate(rate1h.mul(100)),
            fundingRate8hStr: formatNumberFundingRate(rate8h.mul(100)),
            fundingRate1dStr: formatNumberFundingRate(rate1d.mul(100)),
        }
    }, [isLoading, dailyFundingRateCapped])

    return {
        volume24h,
        priceChangeRate24h,
        volume24hStr,
        priceChangeRate24hStr,
        fundingRate1hStr,
        fundingRate8hStr,
        fundingRate1dStr,
    }
}
